// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import type { components } from '@flaircv/api-client';

type AuthResponse = components['schemas']['AuthResponse'];
type LoginChallengeResponse = components['schemas']['LoginChallengeResponse'];

/** A pending login: the password was accepted, the emailed code is still owed. */
export interface LoginChallenge {
  kind: 'challenge';
  challengeToken: string;
  expiresInSeconds: number;
  /** Shown back to the user so they know which inbox to check. */
  email: string;
  /**
   * Kept in memory only, for the vault unlock that runs once the code is
   * accepted. It never leaves the mutation cache and is gone on reload.
   */
  password: string;
}

export type LoginOutcome =
  { kind: 'session'; data: AuthResponse; password: string } | LoginChallenge;

/**
 * The login endpoint answers 200 with a session or 202 with a challenge; the
 * generated client types the body as the union. The challenge is the one
 * with a token, which is the only field the next step cannot do without.
 */
export function toLoginOutcome(
  data: AuthResponse | LoginChallengeResponse,
  email: string,
  password: string,
): LoginOutcome {
  if (isChallengeResponse(data)) {
    return {
      kind: 'challenge',
      challengeToken: data.data.challenge_token,
      expiresInSeconds: data.data.expires_in_seconds,
      email,
      password,
    };
  }
  return { kind: 'session', data, password };
}

function isChallengeResponse(
  data: AuthResponse | LoginChallengeResponse,
): data is LoginChallengeResponse {
  return 'challenge_token' in data.data;
}

/** An error from the code endpoints, with the status the UI keys off. */
export class LoginCodeError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'LoginCodeError';
    this.status = status;
  }
}

/**
 * Whether the server has given up on this challenge, so retyping the code is
 * pointless and the user has to start the login over. 401 is an unknown or
 * consumed challenge; 429 is the guess or resend cap.
 */
export function isChallengeDead(error: unknown): boolean {
  return error instanceof LoginCodeError && (error.status === 401 || error.status === 429);
}

/**
 * The server's 401 text is the generic "Authentication is required.", which
 * on this screen reads as if the user did something wrong. Say what happened.
 */
export function loginCodeErrorMessage(status: number, serverMessage: string): string {
  if (status === 401) {
    return 'This sign-in attempt is no longer valid. Please sign in again.';
  }
  return serverMessage;
}
